import React, { useState } from 'react';
import { X, Save, Eye, EyeOff, Sparkles } from 'lucide-react';
import { ImageUploader } from './ImageUploaders';
import AIBlogPanel from './blog/AIBlogPanel';
import TagInput from './blog/TagInput';

const LABEL_CLS = 'text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1 block';
const INPUT_CLS = 'w-full border border-earth/15 rounded-md px-3 py-2.5 text-sm font-sans text-earth-dark focus:ring-2 focus:ring-saffron/30 focus:outline-none bg-cream/30';

const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const initialForm = (post) => ({
  title: post?.title || '',
  slug: post?.slug || '',
  excerpt: post?.excerpt || '',
  content: post?.content || '',
  cover_image: post?.cover_image || '',
  tags: post?.tags || [],
  published: post?.published ?? false,
});

export default function BlogEditor({ post, onSave, onCancel }) {
  const [form, setForm] = useState(() => initialForm(post));
  const [saving, setSaving] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const setField = (key) => (value) => setForm(f => ({ ...f, [key]: value }));

  const applyAi = (data) => {
    setForm(f => ({
      ...f,
      title: data.title || f.title,
      slug: post ? f.slug : (data.slug || (data.title ? autoSlug(data.title) : f.slug)),
      excerpt: data.excerpt || f.excerpt,
      content: data.content || f.content,
      tags: data.tags?.length ? data.tags : f.tags,
    }));
    setShowAiPanel(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-6 space-y-4" data-testid="blog-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-earth-dark">{post ? 'Edit Post' : 'New Blog Post'}</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAiPanel(s => !s)} className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-sans font-semibold text-xs px-3 py-2 rounded-md hover:from-amber-600 hover:to-orange-600 transition-all shadow-sm" data-testid="ai-blog-toggle">
            <Sparkles size={12} /> AI Generate
          </button>
          <button onClick={onCancel} className="text-earth/40 hover:text-earth transition-colors" data-testid="blog-editor-cancel"><X size={18} /></button>
        </div>
      </div>

      {showAiPanel && <AIBlogPanel onGenerated={applyAi} />}

      <ImageUploader currentImage={form.cover_image} onUpload={setField('cover_image')} label="Upload Cover Image" />
      <div className="mt-2">
        <label className="text-[10px] uppercase tracking-wider text-earth/40 font-sans font-semibold mb-1 block">Or paste image URL</label>
        <input value={form.cover_image} onChange={(e) => setField('cover_image')(e.target.value)} placeholder="https://..." className="w-full border border-earth/10 rounded px-2.5 py-1.5 text-xs font-sans focus:outline-none focus:ring-1 focus:ring-saffron/30 bg-cream/30" data-testid="blog-cover-url" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLS}>Title</label>
          <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value, slug: post ? f.slug : autoSlug(e.target.value) }))} className={INPUT_CLS} data-testid="blog-editor-title" />
        </div>
        <div>
          <label className={LABEL_CLS}>Slug (URL)</label>
          <input value={form.slug} onChange={(e) => setField('slug')(e.target.value)} disabled={!!post} className={`${INPUT_CLS} disabled:opacity-50`} data-testid="blog-editor-slug" />
        </div>
      </div>

      <div>
        <label className={LABEL_CLS}>Excerpt</label>
        <textarea value={form.excerpt} onChange={(e) => setField('excerpt')(e.target.value)} rows={2} className={`${INPUT_CLS} resize-none`} data-testid="blog-editor-excerpt" />
      </div>

      <div>
        <label className={LABEL_CLS}>Content (HTML)</label>
        <textarea value={form.content} onChange={(e) => setField('content')(e.target.value)} rows={10} className={`${INPUT_CLS} text-xs font-mono resize-y`} data-testid="blog-editor-content" />
      </div>

      <TagInput tags={form.tags} onChange={setField('tags')} />

      <div className="flex items-center gap-3">
        <button onClick={() => setForm(f => ({ ...f, published: !f.published }))} className={`flex items-center gap-1.5 text-xs font-sans font-medium px-3 py-2 rounded-md border transition-colors ${form.published ? 'bg-green-50 border-green-200 text-green-700' : 'bg-earth/5 border-earth/15 text-earth/50'}`} data-testid="blog-publish-toggle">
          {form.published ? <Eye size={12} /> : <EyeOff size={12} />}
          {form.published ? 'Published' : 'Draft'}
        </button>
      </div>

      <button onClick={handleSave} disabled={saving || !form.title || !form.slug} className="w-full bg-saffron text-white font-sans font-semibold text-sm py-3 rounded-md hover:bg-saffron-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="blog-editor-save">
        <Save size={14} /> {saving ? 'Saving...' : 'Save Post'}
      </button>
    </div>
  );
}
